import React, { useLayoutEffect, useRef, useState } from "react";
import { t } from "i18next";
import { findEmailPreviewLinkTarget } from "./findEmailPreviewLinkTarget";
import {
  applyEmailPreviewLinkHighlight,
  clearEmailPreviewLinkHighlight,
} from "./emailPreviewLinkHighlight";
import {
  measureEmailPreviewCalloutGeometry,
  getIframeElementPageRect,
  IEmailTopLinkCalloutGeometry,
} from "./emailPreviewLinkCalloutGeometry";

interface IProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  previewHtml: string;
  targetUrl: string;
  label?: string | null;
  clickCount: number;
  rank: number;
}

const PREVIEW_IFRAME_MEASURING_CLASS = "email-builder-preview-iframe--measuring";
const DEFAULT_BADGE_WIDTH_PX = 168;
const DEFAULT_BADGE_HEIGHT_PX = 32;
const MAX_SYNC_ATTEMPTS = 120;

function isIframeReadyForMeasure(iframe: HTMLIFrameElement): boolean {
  return !iframe.classList.contains(PREVIEW_IFRAME_MEASURING_CLASS);
}

function getCalloutBadgeText(rank: number, clickCount: number): string {
  if (rank === 1) {
    return t("Most clicked link · {{count}}", { count: clickCount });
  }

  return t("#{{rank}} clicked link · {{count}}", { rank, count: clickCount });
}

type TCalloutStatus = "locating" | "ready" | "not_found";

export function EmailCampaignTopLinkPreviewCallout(props: IProps) {
  const layerRef = useRef<HTMLDivElement | null>(null);
  const badgeMeasureRef = useRef<HTMLDivElement | null>(null);
  const [geometry, setGeometry] = useState<IEmailTopLinkCalloutGeometry | null>(null);
  const [status, setStatus] = useState<TCalloutStatus>("locating");

  const badgeText = getCalloutBadgeText(props.rank, props.clickCount);

  useLayoutEffect(() => {
    setGeometry(null);
    setStatus("locating");

    let cancelled = false;
    let detachObservers: (() => void) | null = null;
    let attempts = 0;
    let retryTimer: number | null = null;

    function clearRetryTimer() {
      if (retryTimer != null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
    }

    function scheduleRetry(delayMs: number = 50) {
      if (cancelled || retryTimer != null) {
        return;
      }

      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        syncGeometry();
      }, delayMs);
    }

    function syncGeometry() {
      if (cancelled) {
        return;
      }

      attempts += 1;
      const container = props.containerRef.current;
      const overlayLayer = layerRef.current;
      const iframe = container?.querySelector("iframe");
      const doc = iframe?.contentDocument;

      if (container == null || overlayLayer == null || iframe == null || doc == null) {
        if (attempts < MAX_SYNC_ATTEMPTS) {
          scheduleRetry();
        } else {
          setStatus("not_found");
        }
        return;
      }

      if (!isIframeReadyForMeasure(iframe)) {
        if (attempts < MAX_SYNC_ATTEMPTS) {
          scheduleRetry();
        }
        return;
      }

      const target = findEmailPreviewLinkTarget(doc, props.targetUrl, props.label);
      if (target == null) {
        clearEmailPreviewLinkHighlight(doc);
        setGeometry(null);
        setStatus("not_found");
        return;
      }

      applyEmailPreviewLinkHighlight(doc, target);

      const badgeRect = badgeMeasureRef.current?.getBoundingClientRect();
      const badgeWidth = badgeRect != null && badgeRect.width > 0 ? badgeRect.width : DEFAULT_BADGE_WIDTH_PX;
      const badgeHeight = badgeRect != null && badgeRect.height > 0 ? badgeRect.height : DEFAULT_BADGE_HEIGHT_PX;
      const targetPageRect = getIframeElementPageRect(iframe, target);
      const nextGeometry =
        targetPageRect != null
          ? measureEmailPreviewCalloutGeometry(overlayLayer, targetPageRect, badgeWidth, badgeHeight)
          : null;

      if (nextGeometry == null) {
        if (attempts < MAX_SYNC_ATTEMPTS) {
          scheduleRetry();
        } else {
          setStatus("ready");
        }
        return;
      }

      setGeometry(nextGeometry);
      setStatus("ready");
    }

    function attachObservers() {
      detachObservers?.();

      const container = props.containerRef.current;
      const overlayLayer = layerRef.current;
      const iframe = container?.querySelector("iframe");
      const doc = iframe?.contentDocument;
      if (container == null || overlayLayer == null || iframe == null || doc == null) {
        scheduleRetry();
        return;
      }

      const target = findEmailPreviewLinkTarget(doc, props.targetUrl, props.label);

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(syncGeometry);
      });
      resizeObserver.observe(doc.body);
      resizeObserver.observe(iframe);
      resizeObserver.observe(overlayLayer);
      if (target != null) {
        resizeObserver.observe(target);
      }

      const onWindowResize = () => syncGeometry();
      window.addEventListener("resize", onWindowResize);

      const iframeClassObserver = new MutationObserver(() => scheduleRetry(0));
      iframeClassObserver.observe(iframe, { attributes: true, attributeFilter: ["class"] });

      detachObservers = () => {
        resizeObserver.disconnect();
        iframeClassObserver.disconnect();
        window.removeEventListener("resize", onWindowResize);
      };

      syncGeometry();
    }

    function handleIframeReady() {
      attachObservers();
    }

    const container = props.containerRef.current;
    const iframe = container?.querySelector("iframe") ?? null;
    if (iframe != null) {
      iframe.addEventListener("load", handleIframeReady);
      handleIframeReady();
    } else {
      scheduleRetry();
    }

    return () => {
      cancelled = true;
      clearRetryTimer();
      iframe?.removeEventListener("load", handleIframeReady);
      detachObservers?.();
      const doc = props.containerRef.current?.querySelector("iframe")?.contentDocument ?? null;
      clearEmailPreviewLinkHighlight(doc);
    };
  }, [badgeText, props.containerRef, props.label, props.previewHtml, props.targetUrl]);

  return (
    <div ref={layerRef} className="email-campaign-top-link-preview__callout-layer">
      <div
        ref={badgeMeasureRef}
        className="email-campaign-top-link-preview__badge email-campaign-top-link-preview__badge--measure"
        aria-hidden="true"
      >
        {badgeText}
      </div>

      {geometry ? (
        <>
          <svg className="email-campaign-top-link-preview__connector" aria-hidden="true">
            <line
              x1={geometry.line.x1}
              y1={geometry.line.y1}
              x2={geometry.line.x2}
              y2={geometry.line.y2}
            />
          </svg>
          <div
            className="email-campaign-top-link-preview__badge"
            style={{
              top: `${geometry.badge.top}px`,
              left: `${geometry.badge.left}px`,
            }}
          >
            {badgeText}
          </div>
        </>
      ) : null}

      {status === "locating" ? (
        <div className="email-campaign-top-link-preview__missing-link">
          {t("Locating link in preview…")}
        </div>
      ) : null}

      {status === "not_found" ? (
        <div className="email-campaign-top-link-preview__missing-link">
          {t("This link could not be located in the email preview yet.")}
        </div>
      ) : null}
    </div>
  );
}
